import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
    Tooltip
} from "@mui/material";
import {
    FactCheck as CheckInIcon,
    Add as AddIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    Straighten as MeasurementsIcon,
    MonitorWeight as WeightIcon,
    CameraAlt as PhotoIcon,
    Description as FormIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMessageContext } from "../../contexts/MessageContext";
import { getErrorMessage } from "../../enums/ErrorMessages";
import { getTraineeCheckIns, deleteCheckIn, createCheckIn } from "../../services/CheckInService";
import CreateCheckInDialog from "../check-ins/CreateCheckInDialog";
import {colors, borders, shadows} from "../ui/colors";
import {t as coachT} from "../dashboard/coach/coachTokens";
import {StatusChip} from "../ui";
import {RTL} from "../../cache/CacheRtl";

const ITEM_TYPE_ICONS = {
    BODY_MEASUREMENT: { icon: <MeasurementsIcon sx={{ fontSize: "16px" }} />, color: "#8B5CF6" },
    BODY_WEIGHT: { icon: <WeightIcon sx={{ fontSize: "16px" }} />, color: colors.info },
    PROGRESS_PHOTO: { icon: <PhotoIcon sx={{ fontSize: "16px" }} />, color: colors.success },
    FORM: { icon: <FormIcon sx={{ fontSize: "16px" }} />, color: colors.warning }
};

const ITEM_TYPE_COACH = {
    BODY_MEASUREMENT: coachT.purple,
    BODY_WEIGHT: coachT.blue,
    PROGRESS_PHOTO: coachT.green,
    FORM: coachT.amber,
};

const STATUS_CONFIG = {
    PENDING: { variant: "warning", label: "ממתין למילוי" },
    COMPLETED: { variant: "success", label: "הושלם" },
    REVIEWED: { variant: "default", label: "נבדק" },
    CANCELED: { variant: "error", label: "בוטל" }
};

const formatDate = (epochSeconds) => {
    if (!epochSeconds) return null;
    return new Date(epochSeconds * 1000).toLocaleDateString('he-IL');
};

const tdBase = {borderBottom: borders.default, py: 0, px: 2, height: "56px", fontSize: "13px", color: colors.textPrimary};
const thBase = {...tdBase, height: "42px", color: colors.textMuted, fontSize: "11px", fontWeight: 600, backgroundColor: colors.bg, letterSpacing: "0.3px"};

const tdCoach = {borderBottom: `1px solid ${coachT.borderLight}`, py: 0, px: 2, height: "56px", fontSize: "13px", color: coachT.text};
const thCoach = {...tdCoach, height: "42px", color: coachT.t3, fontSize: "10.5px", fontWeight: 700, backgroundColor: coachT.bg, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${coachT.border}`};

const ClientPageCheckIns = ({ client, coach = false }) => {
    const th = coach ? thCoach : thBase;
    const td = coach ? tdCoach : tdBase;
    const { notificationHandler } = useMessageContext();
    const navigate = useNavigate();

    const [checkIns, setCheckIns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);

    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    const fetchCheckIns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getTraineeCheckIns(client.id);
            setCheckIns(res.data || []);
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה בטעינת הצ'ק-אינים");
            setCheckIns([]);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id]);

    useEffect(() => {
        fetchCheckIns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client.id, reload]);

    const handleReload = () => setReload(prev => !prev);

    const handleCreateSubmit = async (data) => {
        await createCheckIn(data);
        notificationHandler.success("צ'ק-אין נשלח בהצלחה");
        handleReload();
    };

    const handleView = (checkIn) => {
        navigate(`/app/check-ins/${checkIn.id}`);
    };

    const handleDelete = async (checkIn) => {
        try {
            await deleteCheckIn(checkIn.id);
            notificationHandler.success("צ'ק-אין נמחק בהצלחה");
            handleReload();
        } catch (e) {
            notificationHandler.error(getErrorMessage(e.response?.data?.errorCode) || "שגיאה במחיקת הצ'ק-אין");
        }
    };

    const getStatusChipForCheckIn = (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
        return <StatusChip variant={config.variant} label={config.label} />;
    };

    return (
        <>
            <Box sx={coach ? {} : { margin: "20px" }}>
                <RTL>
                <Paper
                    dir="rtl"
                    sx={{
                        backgroundColor: coach ? coachT.white : colors.white,
                        border: coach ? `1px solid ${coachT.border}` : borders.default,
                        borderRadius: coach ? coachT.r : borders.radius.lg,
                        boxShadow: coach ? coachT.sh : shadows.card,
                        overflow: "hidden",
                    }}
                    elevation={0}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: coach ? "16px 22px" : "16px 20px",
                            backgroundColor: coach ? coachT.bg : colors.bg,
                            borderBottom: coach ? `1px solid ${coachT.border}` : borders.default
                        }}
                    >
                        <Box sx={{display: "flex", alignItems: "center", gap: coach ? 1.5 : 1}}>
                            {coach ? (
                                <Box sx={{width: 40, height: 40, borderRadius: "11px", background: coachT.white, border: `1px solid ${coachT.border}`, display: "flex", alignItems: "center", justifyContent: "center"}}>
                                    <CheckInIcon sx={{color: coachT.t2, fontSize: 22}}/>
                                </Box>
                            ) : (
                                <CheckInIcon sx={{ color: colors.textSecondary, fontSize: "20px" }} />
                            )}
                            <Box>
                                <Typography
                                    sx={{
                                        fontWeight: coach ? 800 : 600,
                                        fontSize: coach ? "17px" : "16px",
                                        color: coach ? coachT.text : colors.textPrimary,
                                        letterSpacing: coach ? "-0.2px" : 0,
                                    }}
                                >
                                    צ'ק-אין{coach ? "" : ` - ${client?.name}`}
                                </Typography>
                                {!coach && (
                                    <Typography sx={{fontSize: "14px", color: colors.textSecondary, marginTop: "4px"}}>
                                        כל הצ'ק-אינים של המתאמן
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {coach ? (
                            <Box component="button" onClick={() => setCreateDialogOpen(true)} sx={{
                                display: "inline-flex", alignItems: "center", gap: "5px",
                                background: coachT.green, color: "#fff",
                                height: 34, px: "15px", borderRadius: coachT.rs,
                                fontSize: "13px", fontWeight: 600,
                                border: "none", cursor: "pointer", fontFamily: "inherit",
                                transition: "all .14s",
                                "&:hover": {background: coachT.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                            }}>
                                <AddIcon sx={{fontSize: 16}}/>
                                שליחת צ'ק-אין
                            </Box>
                        ) : (
                            <Button
                                onClick={() => setCreateDialogOpen(true)}
                                variant="contained"
                                startIcon={<AddIcon />}
                                sx={{
                                    backgroundColor: colors.primary,
                                    color: colors.white,
                                    fontSize: "13px",
                                    padding: "8px 16px",
                                    "&:hover": { backgroundColor: colors.primaryDark }
                                }}
                                size="small"
                            >
                                שליחת צ'ק-אין
                            </Button>
                        )}
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: coach ? "16px 22px" : "20px" }}>
                        {loading ? (
                            <Box sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "200px"
                            }}>
                                <CircularProgress sx={{ color: coach ? coachT.green : colors.primary }} />
                            </Box>
                        ) : checkIns.length > 0 ? (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            {["תאריך שליחה", "סטטוס", "פריטים", "התקדמות"].map((header) => (
                                                <TableCell key={header} sx={th}>
                                                    {header}
                                                </TableCell>
                                            ))}
                                            <TableCell sx={th} style={{textAlign: "left"}}>פעולות</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {checkIns.map((checkIn) => {
                                            const completedCount = checkIn.items?.filter(i => i.completed).length || 0;
                                            const totalCount = checkIn.items?.length || 0;
                                            return (
                                                <TableRow
                                                    key={checkIn.id}
                                                    onClick={() => handleView(checkIn)}
                                                    sx={{
                                                        cursor: "pointer",
                                                        "&:hover": { backgroundColor: coach ? coachT.greenLight : colors.bg },
                                                        transition: "background-color 0.2s"
                                                    }}
                                                >
                                                    <TableCell sx={{...td, color: coach ? coachT.t2 : colors.textSecondary}}>
                                                        {formatDate(checkIn.createdDate) || "-"}
                                                    </TableCell>
                                                    <TableCell sx={td}>
                                                        {getStatusChipForCheckIn(checkIn.status)}
                                                    </TableCell>
                                                    <TableCell sx={td}>
                                                        <Box sx={{ display: "flex", gap: 0.5 }}>
                                                            {checkIn.items?.map((item, idx) => {
                                                                const config = ITEM_TYPE_ICONS[item.type];
                                                                if (!config) return null;
                                                                const iconColor = coach ? (ITEM_TYPE_COACH[item.type] || coachT.t2) : config.color;
                                                                return (
                                                                    <Box key={idx} sx={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        borderRadius: coach ? coachT.rxs : borders.radius.sm,
                                                                        backgroundColor: `${iconColor}15`,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        "& svg": { color: item.completed ? iconColor : (coach ? coachT.t3 : colors.borderHover) }
                                                                    }}>
                                                                        {config.icon}
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{...td, fontWeight: 600, color: coach ? coachT.t2 : colors.textSecondary}}>
                                                        {completedCount}/{totalCount}
                                                    </TableCell>
                                                    <TableCell sx={td}>
                                                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                                                            <Tooltip title="צפייה">
                                                                <IconButton
                                                                    onClick={() => handleView(checkIn)}
                                                                    size="small"
                                                                    sx={{
                                                                        color: coach ? coachT.t2 : colors.primary,
                                                                        "&:hover": { backgroundColor: coach ? coachT.greenLight : colors.primaryLight, color: coach ? coachT.green : undefined }
                                                                    }}
                                                                >
                                                                    <ViewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="מחיקה">
                                                                <IconButton
                                                                    onClick={() => handleDelete(checkIn)}
                                                                    size="small"
                                                                    sx={{
                                                                        color: coach ? coachT.t2 : colors.error,
                                                                        "&:hover": { backgroundColor: coach ? coachT.redLight : colors.errorBg, color: coach ? coachT.red : undefined }
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "200px",
                                flexDirection: "column",
                                gap: 2
                            }}>
                                <CheckInIcon sx={{ fontSize: "48px", color: coach ? coachT.border : colors.borderHover }} />
                                <Typography sx={{
                                    fontSize: "16px",
                                    fontWeight: 500,
                                    color: coach ? coachT.t3 : colors.textSecondary,
                                    textAlign: "center"
                                }}>
                                    אין עדיין צ'ק-אינים למתאמן הזה
                                </Typography>
                                <Typography sx={{
                                    fontSize: "14px",
                                    color: coach ? coachT.t3 : colors.textMuted,
                                    textAlign: "center"
                                }}>
                                    לחץ על "שליחת צ'ק-אין" כדי לשלוח צ'ק-אין ראשון
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
                </RTL>
            </Box>

            {/* Dialogs */}
            <CreateCheckInDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSubmit={handleCreateSubmit}
                preSelectedTrainee={{ id: client.id, name: client.name }}
                coach={coach}
            />

        </>
    );
};

export default ClientPageCheckIns;